import { useContext, useState } from 'react'
import { Box, Button, Collapse, Paper, Typography } from '@mui/material'


import { VibioThemeContext } from "../../../context/VibioThemeProvider"

import BouncingArrowIcon from '../profile/BouncingArrowIcon'
import { SocialLinkList } from '../../common/domain/sharable/videopage/SocialLinkList'
import { Profile } from '../profile/Profile'


export default function ProfilePreviewCard({ userData, socials, linkButtons }) {
    // Theme is seeded + kept live by StudioPage/VibioThemeProvider, so edits show instantly here.
    const { theme } = useContext(VibioThemeContext)
    const [isProfileExpanded, setProfileExpanded] = useState(true)
    const toggleProfile = () => setProfileExpanded(!isProfileExpanded)

    if (!userData || !theme) {
        return <Typography>Loading user data...</Typography>
    }

    const isLightColor = (color) => {
        const r = parseInt(color.substring(0, 2), 16) / 255
        const g = parseInt(color.substring(2, 4), 16) / 255
        const b = parseInt(color.substring(4, 6), 16) / 255

        const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
        return luminance > 0.7
    }

    const textColor = isLightColor(theme?.buttonColor) ? '#040404' : '#f6f6f6'
    const bioTextColor = isLightColor(theme?.bgColor) ? '#0c0c0c' : '#fff'

    const fontStyles = {
        REGULAR: 'Catamaran',
        ELEGANT: 'Times New Roman',
        BOLD: 'Arial Black',
        MONO: 'monospace',
        PLAYFUL: 'Comic Sans MS',
    }


    const buttonStyles = {
        FILLED_REGULAR: { backgroundColor: `#${theme.buttonColor}`, borderRadius: 0 },
        FILLED_ROUND: { backgroundColor: `#${theme.buttonColor}`, borderRadius: '32px' },
        OUTLINE_REGULAR: {
            backgroundColor: 'transparent',
            border: `3px solid #${theme.buttonColor}`,
            color: "#000",
            borderRadius: 0
        },
        OUTLINE_ROUND: {
            backgroundColor: 'transparent',
            border: `3px solid #${theme.buttonColor}`,
            borderRadius: '32px',
            color: "#000",
        },
        SHADOW_REGULAR: {
            backgroundColor: `#${theme.buttonColor}`,
            boxShadow: '3px 3px 2px 3px #000',
            borderRadius: 0
        },
        SHADOW_ROUND: {
            backgroundColor: `#${theme.buttonColor}`,
            boxShadow: '3px 3px 2px 3px #000',
            borderRadius: '32px',
        },
    }

    return (
        <Button onClick={toggleProfile}
            sx={{
                width: '100%',
                padding: 0,
                textTransform: 'none',
                color: 'text.second'
            }}>
            <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: '.2rem',
                right: '.2rem',
                paddingBottom: '1rem',
                backgroundColor: 'transparent',
                borderRadius: '20px',
                maxHeight: '88vh',
            }}>
                <div style={{
                    textAlign: 'center',
                    width: '100%',
                }}>
                    <Typography
                        variant="body1"
                        component="p"
                        sx={{
                            fontWeight: 400,
                            fontFamily: 'Catamaran',
                            textAlign: 'center',
                            textTransform: 'none',
                            fontSize: '70%',
                            p: .4,
                        }}
                    >
                        {isProfileExpanded ? '' : 'tap to expand'}
                    </Typography>
                </div>

                {/* profile */}
                <Profile
                    isFullView={isProfileExpanded}
                    userData={userData}
                    color={theme?.buttonColor}
                    bioTxtColor={bioTextColor}
                    fontColor={textColor}
                />

                {/* social links */}
                <SocialLinkList
                    socials={socials}
                    iconColor={theme?.iconColor}
                />

                {!isProfileExpanded && <BouncingArrowIcon />}

                {/* link buttons */}
                <Collapse in={isProfileExpanded}>
                    <div style={{
                        overflowY: 'auto',
                        maxHeight: '50vh',
                    }}>
                        {
                            linkButtons
                                .sort((a, b) => a.position - b.position)
                                .map((btn) => (
                                    <Button
                                        href={btn.url.startsWith("http") ? btn.url : `https://${btn.url}`}
                                        key={btn.id}
                                        sx={{
                                            display: 'block',
                                            color: textColor,
                                            ":hover": {
                                                backgroundColor: '#FF1493',
                                            },
                                            m: '1rem 1.5rem',
                                            p: '.6rem',
                                            maxWidth: '100%',
                                            boxSizing: 'border-box',
                                            ...buttonStyles[theme.buttonStyle]
                                        }}>
                                        <Typography
                                            variant="body1"
                                            component="p"
                                            sx={{
                                                fontWeight: 500,
                                                fontFamily: `${fontStyles[theme.fontStyle]}`,
                                                textAlign: 'center',
                                                textTransform: 'none',
                                                // Keep long titles from overflowing: break long tokens, clamp to 2 lines.
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {btn.title}
                                        </Typography>
                                    </Button>
                                ))
                        }
                    </div>
                    {/* Vibio signature */}
                    {userData?.tier === 'LIGHT' && (
                        <Paper
                            elevation={4}
                            sx={{
                                m: '.8rem 0',
                                p: 0,
                                display: 'inline-block',
                                borderRadius: '40px',
                            }}
                        >
                            <p style={{
                                padding: '0 1rem',
                                margin: '.2rem 0',
                                alignSelf: 'center',
                                fontSize: '1rem',
                                fontWeight: 500,
                                fontFamily: 'Catamaran',
                            }}>
                                <a href='https://vibio.co' style={{ textDecoration: 'none', color: "#000" }}>
                                    Join <b>{userData.username}</b> on Vibio
                                </a>
                            </p>
                        </Paper>
                    )}
                </Collapse>
            </Box>
        </Button>
    )
}
